import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './InvestmentLedgerPage.css';

import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Chip,
  Alert,
  Container,
  Stack,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const FILTERS = ['ALL', 'BUY', 'SELL'];

function InvestmentLedgerPage() {
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (filter === 'ALL') {
      setFilteredTransactions(transactions);
    } else {
      setFilteredTransactions(transactions.filter((txn) => txn.buy_sell === filter));
    }
  }, [transactions, filter]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/investments/transactions');
      if (response.data && response.data.transactions) {
        setTransactions(response.data.transactions);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      setError('Error loading transactions: ' + (err.response?.data?.detail || err.message));
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const buyCount = transactions.filter((t) => t.buy_sell === 'BUY').length;
  const sellCount = transactions.filter((t) => t.buy_sell === 'SELL').length;

  const columns = [
    'Date',
    'Ticker',
    'Type',
    'Investment Type',
    'Quantity',
    'Price',
    'Contract Value',
    'Commission',
    'GST',
    'Total Proceed',
    'Net Cash Flow',
    'Currency',
    'Broker',
    'Status',
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="xl">
          {/* Page Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 4,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}
              >
                Investment Ledger
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Full transaction history for all investment activity
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={() => navigate('/investments/holdings')}
                sx={{
                  borderColor: '#6366f1',
                  color: '#6366f1',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { borderColor: '#4f46e5', bgcolor: 'rgba(99,102,241,0.06)' },
                }}
              >
                View Holdings
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/investments/log')}
                sx={{
                  bgcolor: '#6366f1',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#4f46e5', boxShadow: 'none' },
                }}
              >
                Log Transaction
              </Button>
            </Stack>
          </Box>

          {/* Summary Stat Cards */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Transactions', value: transactions.length, color: '#6366f1' },
              { label: 'BUY Orders', value: buyCount, color: '#22c55e' },
              { label: 'SELL Orders', value: sellCount, color: '#ef4444' },
            ].map((stat) => (
              <Paper
                key={stat.label}
                elevation={0}
                sx={{
                  flex: 1,
                  px: 3,
                  py: 2.5,
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)',
                  borderRadius: 2,
                  borderLeft: `4px solid ${stat.color}`,
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {stat.value}
                </Typography>
              </Paper>
            ))}
          </Stack>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
          ) : (
            <>
              {/* Controls Bar */}
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  px: 3,
                  py: 2,
                  mb: 2,
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)',
                  borderRadius: 2,
                }}
              >
                {/* Filter Chips */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mr: 0.5 }}>
                    Filter:
                  </Typography>
                  {FILTERS.map((f) => (
                    <Chip
                      key={f}
                      label={f}
                      onClick={() => setFilter(f)}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        bgcolor:
                          filter === f
                            ? f === 'BUY'
                              ? 'rgba(34,197,94,0.15)'
                              : f === 'SELL'
                              ? 'rgba(239,68,68,0.15)'
                              : 'rgba(99,102,241,0.2)'
                            : 'rgba(255,255,255,0.04)',
                        color:
                          filter === f
                            ? f === 'BUY'
                              ? 'success.main'
                              : f === 'SELL'
                              ? 'error.main'
                              : '#818cf8'
                            : 'text.secondary',
                        border:
                          filter === f
                            ? `1px solid ${
                                f === 'BUY' ? 'rgba(34,197,94,0.4)' : f === 'SELL' ? 'rgba(239,68,68,0.4)' : 'rgba(99,102,241,0.4)'
                              }`
                            : '1px solid transparent',
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  ))}
                </Stack>

                {/* Row count */}
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Showing{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {filteredTransactions.length}
                  </Box>{' '}
                  transaction{filteredTransactions.length !== 1 ? 's' : ''}
                </Typography>
              </Paper>

              {/* Empty State */}
              {filteredTransactions.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    textAlign: 'center',
                    py: 10,
                    px: 4,
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'var(--tx-lo)', mb: 1 }}>
                    No transactions to display
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 3 }}>
                    {filter !== 'ALL'
                      ? `No ${filter} transactions found. Try a different filter.`
                      : 'Get started by logging your first investment transaction.'}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/investments/log')}
                    sx={{
                      bgcolor: '#6366f1',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#4f46e5', boxShadow: 'none' },
                    }}
                  >
                    Log Your First Transaction
                  </Button>
                </Paper>
              ) : (
                /* Data Table */
                <Paper
                  elevation={0}
                  sx={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', borderRadius: 2, overflow: 'hidden' }}
                >
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 1600 }} size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'var(--bg-card)' }}>
                          {columns.map((col) => (
                            <TableCell
                              key={col}
                              sx={{
                                color: 'var(--tx-lo)',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                whiteSpace: 'nowrap',
                                py: 1.75,
                                px: 2,
                                borderBottom: '2px solid #334155',
                              }}
                            >
                              {col}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTransactions.map((txn, index) => {
                          const isBuy = txn.buy_sell === 'BUY';
                          const cashFlow = parseFloat(txn.net_cash_flow);
                          const isPositiveCashFlow = cashFlow >= 0;
                          const rowBg = isBuy
                            ? 'rgba(34,197,94,0.04)'
                            : 'rgba(239,68,68,0.04)';
                          const rowHoverBg = isBuy
                            ? 'rgba(34,197,94,0.09)'
                            : 'rgba(239,68,68,0.09)';

                          return (
                            <TableRow
                              key={index}
                              sx={{
                                bgcolor: rowBg,
                                '&:hover': { bgcolor: rowHoverBg },
                                '&:last-child td': { borderBottom: 'none' },
                              }}
                            >
                              {/* Date */}
                              <TableCell
                                sx={{
                                  whiteSpace: 'nowrap',
                                  fontWeight: 500,
                                  color: 'text.secondary',
                                  fontSize: '0.85rem',
                                  py: 1.5,
                                  px: 2,
                                  borderBottom: '1px solid',
                                }}
                              >
                                {formatDate(txn.date_purchase_sold)}
                              </TableCell>

                              {/* Ticker */}
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: '#6366f1',
                                  fontSize: '0.875rem',
                                  py: 1.5,
                                  px: 2,
                                  borderBottom: '1px solid',
                                }}
                              >
                                {txn.investment_name}
                              </TableCell>

                              {/* Type Chip */}
                              <TableCell sx={{ py: 1.5, px: 2, borderBottom: '1px solid' }}>
                                <Chip
                                  label={txn.buy_sell}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 22,
                                    bgcolor: isBuy ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                    color: isBuy ? 'success.main' : 'error.main',
                                    border: `1px solid ${isBuy ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
                                  }}
                                />
                              </TableCell>

                              {/* Investment Type */}
                              <TableCell
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.85rem',
                                  py: 1.5,
                                  px: 2,
                                  borderBottom: '1px solid',
                                }}
                              >
                                {txn.investment_type}
                              </TableCell>

                              {/* Numeric Cells */}
                              {[
                                txn.quantity,
                                txn.price_filled,
                                txn.total_contract_value,
                                txn.commission,
                                txn.gst,
                                txn.total_proceed,
                              ].map((val, i) => (
                                <TableCell
                                  key={i}
                                  align="right"
                                  sx={{
                                    fontFamily: '"Roboto Mono", "Courier New", monospace',
                                    fontSize: '0.82rem',
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    whiteSpace: 'nowrap',
                                    py: 1.5,
                                    px: 2,
                                    borderBottom: '1px solid',
                                  }}
                                >
                                  {formatNumber(val)}
                                </TableCell>
                              ))}

                              {/* Net Cash Flow */}
                              <TableCell
                                align="right"
                                sx={{
                                  fontFamily: '"Roboto Mono", "Courier New", monospace',
                                  fontSize: '0.82rem',
                                  fontWeight: 700,
                                  color: isPositiveCashFlow ? 'success.main' : 'error.main',
                                  whiteSpace: 'nowrap',
                                  py: 1.5,
                                  px: 2,
                                  borderBottom: '1px solid',
                                }}
                              >
                                {formatNumber(txn.net_cash_flow)}
                              </TableCell>

                              {/* Currency */}
                              <TableCell
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  py: 1.5,
                                  px: 2,
                                  borderBottom: '1px solid',
                                }}
                              >
                                {txn.currency}
                              </TableCell>

                              {/* Broker */}
                              <TableCell
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.85rem',
                                  py: 1.5,
                                  px: 2,
                                  borderBottom: '1px solid',
                                }}
                              >
                                {txn.broker}
                              </TableCell>

                              {/* Status */}
                              <TableCell sx={{ py: 1.5, px: 2, borderBottom: '1px solid' }}>
                                <Chip
                                  label={txn.status}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    height: 22,
                                    bgcolor: 'rgba(255,255,255,0.04)',
                                    color: 'text.secondary',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </>
          )}
        </Container>
      </Box>
  );
}

export default InvestmentLedgerPage;
